import { useMemo, useRef } from 'react';
import { highlightMermaidHtml } from './mermaidTextUtils';

interface MermaidCodeEditorProps {
  value: string;
  readOnly?: boolean;
  softWrap?: boolean;
  palette: {
    panelBg: string;
    panelBorder: string;
    panelText: string;
  };
  placeholder?: string;
  onChange?: (value: string) => void;
}

export function MermaidCodeEditor({
  value,
  readOnly = false,
  softWrap = true,
  palette,
  placeholder,
  onChange,
}: MermaidCodeEditorProps) {
  const previewRef = useRef<HTMLPreElement | null>(null);
  const highlightedHtml = useMemo(() => highlightMermaidHtml(value), [value]);
  const whiteSpace = softWrap ? 'pre-wrap' : 'pre';

  return (
    <div
      className="relative flex-1 min-h-0 border rounded-xl overflow-hidden"
      style={{ borderColor: palette.panelBorder, background: 'rgba(15,23,42,0.32)' }}
    >
      <pre
        ref={previewRef}
        className="absolute inset-0 m-0 px-3 py-2.5 text-[13px] leading-6 font-mono pointer-events-none overflow-auto"
        style={{ color: palette.panelText, whiteSpace, wordBreak: softWrap ? 'break-word' : 'normal' }}
        dangerouslySetInnerHTML={{ __html: highlightedHtml || '&nbsp;' }}
      />
      <textarea
        readOnly={readOnly}
        className="absolute inset-0 w-full h-full px-3 py-2.5 text-[13px] leading-6 font-mono resize-none focus:outline-none"
        style={{
          whiteSpace,
          color: 'transparent',
          caretColor: palette.panelText,
          background: 'transparent',
          overflow: 'auto',
        }}
        placeholder={placeholder}
        value={value}
        onScroll={(event) => {
          if (!previewRef.current) return;
          previewRef.current.scrollTop = event.currentTarget.scrollTop;
          previewRef.current.scrollLeft = event.currentTarget.scrollLeft;
        }}
        onChange={(event) => {
          if (!onChange) return;
          onChange(event.target.value);
        }}
      />
    </div>
  );
}
